import React, { useState } from 'react';
import { useUserProfileQuery } from '@framework/customer/use-update-customer';
import { useTranslation } from 'next-i18next';
import { IoCopyOutline } from 'react-icons/io5';
import UserDashboardCardLoader from '@components/ui-old/loaders/user-card-loader';
import { ToastProvider, Toast, ToastTitle, ToastClose, ToastViewport } from '@/components/ui/toast';
import { CircleCheckIcon, XIcon } from 'lucide-react';
import { useProgressTimer } from '@/hooks/use-progress-timer';
const UserDashboardCard: React.FC = () => {
  // Toast notifications state/hooks
  const [toastOpen, setToastOpen] = useState(false);
  const toastDuration = 2000;
  const {
    progress,
    start,
    pause,
    resume
  } = useProgressTimer({
    duration: toastDuration,
    onComplete: () => setToastOpen(false)
  });
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const {
    t
  } = useTranslation();
  const {
    data: user,
    isLoading: isUserLoading
  } = useUserProfileQuery();
  if (isUserLoading) {
    return <UserDashboardCardLoader />;
  }
  const formatFboId = (fboId: string): string => {
    return fboId.replace(/(\d{3})(?=\d)/g, '$1-');
  };
  const handleCopyFboId = (fboId: string) => {
    const originalFboId = fboId.replace(/-/g, '');
    navigator.clipboard.writeText(originalFboId).then(() => {
      setToastType('success');
      setToastMessage(t('text-fboid-copy-success'));
      setToastOpen(true);
      start();
    }).catch(() => {
      setToastType('error');
      setToastMessage(t('text-fboid-copy-failed'));
      setToastOpen(true);
      start();
    });
  };
  const formatLevel = (level: string): string => {
    return level.split('_').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  };
  return <ToastProvider swipeDirection="right" data-sentry-element="ToastProvider" data-sentry-component="UserDashboardCard" data-sentry-source-file="user-card.tsx">
      <>
        <div className="p-4 sm:p-6 flex flex-row items-center space-x-4 max-w-full sm:max-w-[500px] rounded-2xl border border-transparent bg-neutral-50 dark:bg-zinc-800 transition duration-150 ease-in-out hover:border-neutral-500/20 dark:bg-zinc-900">
          <img src={user?.avatar || '/assets/images/default-avatar.png'} alt="User Avatar" className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover bg-neutral-100 dark:bg-zinc-700" />
          <div className="flex flex-col w-full">
            <span className="text-lg sm:text-xl text-neutral-800 dark:text-neutral-200 font-semibold mb-1">
              {`${user?.firstName || ''} ${user?.lastName || ''}`}
            </span>
            <span className="text-xs sm:text-base font-regular bg-yellow-300 text-yellow-800 px-2 py-0.5 rounded-full shadow w-fit">
              {formatLevel(user?.level || '')}
            </span>
            <div className="mt-2 sm:mt-4">
              <span className="text-xs sm:text-sm text-neutral-800 dark:text-neutral-200 block mb-1">
                {t('common:text-fbo-id')}
              </span>
              <div className="flex items-center space-x-2">
                <span className="text-sm sm:text-base text-neutral-800 dark:text-neutral-200">
                  {formatFboId(user?.fboIdnumber || '')}
                </span>
                <button type="button" onClick={() => handleCopyFboId(user?.fboIdnumber || '')} className="text-neutral-800 dark:text-neutral-200 hover:text-neutral-500 dark:hover:text-neutral-400" aria-label="Copy FBO ID">
                  <IoCopyOutline size={16} data-sentry-element="IoCopyOutline" data-sentry-source-file="user-card.tsx" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
      <Toast open={toastOpen} onOpenChange={open => {
      setToastOpen(open);
      if (open) start();
    }} onPause={pause} onResume={resume} data-sentry-element="Toast" data-sentry-source-file="user-card.tsx">
        <div className="flex w-full justify-between gap-3">
          {toastType === 'success' ? <CircleCheckIcon size={16} className="mt-0.5 text-emerald-500" /> : <XIcon size={16} className="mt-0.5 text-red-500" />}
          <div className="flex flex-col flex-grow gap-3">
            <ToastTitle data-sentry-element="ToastTitle" data-sentry-source-file="user-card.tsx">{toastMessage}</ToastTitle>
          </div>
          <ToastClose data-sentry-element="ToastClose" data-sentry-source-file="user-card.tsx">
            <button className="p-1"><XIcon size={16} data-sentry-element="XIcon" data-sentry-source-file="user-card.tsx" /></button>
          </ToastClose>
        </div>
        <div className={`absolute -left-2 right-0 bottom-0 h-1 ${toastType === 'success' ? 'bg-emerald-500' : 'bg-red-500'}`} style={{
        width: `${progress / toastDuration * 100}%`,
        transition: 'width 100ms linear'
      }} />
      </Toast>
      <ToastViewport className="fixed top-4 right-4 z-[9999] sm:bottom-4 sm:top-auto" data-sentry-element="ToastViewport" data-sentry-source-file="user-card.tsx" />
    </ToastProvider>;
};
export default UserDashboardCard;